import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { AddedToCartDialogComponent } from '@spartacus/cart/base/components';
import { ActiveCartFacade, Cart, CartAddEntryFailEvent, CartAddEntrySuccessEvent, OrderEntry } from '@spartacus/cart/base/root';
import { isNotUndefined, RoutingService, WindowRef } from '@spartacus/core';
import { LaunchDialogService } from '@spartacus/storefront';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CustomValidateCartService } from '../../../../../../services/cart/validate-cart.service';

export interface AddedToCartDialogComponentData {
  subEntry: OrderEntry;
  productCode: string;
  quantity: number;
  numberOfEntriesBeforeAdd: number;
  pickupStoreName: string;
  addingEntryResult$?: Observable<CartAddEntrySuccessEvent | CartAddEntryFailEvent>;
}

@Component({
  selector: 'app-custom-added-to-cart-dialog',
  templateUrl: './custom-added-to-cart-dialog.component.html',
  styleUrls: ['./custom-added-to-cart-dialog.component.scss'],
  standalone: false
})
export class CustomAddedToCartDialogComponent extends AddedToCartDialogComponent implements OnInit, OnDestroy {
  activeCartZone$ = this.cart$.pipe(map((data) => (!!data?.deliveryZone?.code ? data?.deliveryZone : 'continentalEurope')));
  displayEntry$?: BehaviorSubject<OrderEntry>;

  private destroy$ = new Subject<void>();
  private dialogCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    protected activeCartFacade: ActiveCartFacade,
    protected launchDialogService: LaunchDialogService,
    protected routingService: RoutingService,
    protected el: ElementRef,
    protected validateCart: CustomValidateCartService,
    protected cd: ChangeDetectorRef,
    protected winRef: WindowRef
  ) {
    super(activeCartFacade, launchDialogService, routingService, el);
  }

  ngOnInit(): void {
    if (this.winRef.isBrowser()) {
      this.initializeDialog();
    }
    this.subscription.add(
      this.launchDialogService.data$.pipe(take(1)).subscribe((dialogData: AddedToCartDialogComponentData) => {
        this.displayEntry$ = new BehaviorSubject(dialogData.subEntry);
        this.init(dialogData.productCode, dialogData.quantity, dialogData.numberOfEntriesBeforeAdd, dialogData.pickupStoreName, dialogData.addingEntryResult$);
      })
    );
    this.subscription.add(
      this.routingService
        .getRouterState()
        .pipe(filter((state) => !!state.nextState))
        .subscribe(() => this.dismissModal('dismiss'))
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.displayEntry$?.complete();
    this.stopTimer();
    this.subscription.unsubscribe();
  }

  init(
    productCode: string,
    quantity: number,
    numberOfEntriesBeforeAdd: number,
    pickupStoreName?: string,
    addingEntryResult$?: Observable<CartAddEntrySuccessEvent | CartAddEntryFailEvent>,
    subEntry?: OrderEntry
  ): void {
    // Display last entry for new product code. This always corresponds to
    // our new item, independently of whether merging occured or not
    const productCode$: Observable<string> = addingEntryResult$
      ? // get the product code from the backend response, because it might be different
        // from the requested product code. That can e.g. happen for certain kinds of product variants
        addingEntryResult$.pipe(
          filter((event) => event instanceof CartAddEntrySuccessEvent),
          map((event) => {
            const productCodeFromEntry = (event as CartAddEntrySuccessEvent).entry?.product?.code;
            return productCodeFromEntry ? productCodeFromEntry : event.productCode;
          })
        )
      : of(productCode);
    this.entry$ = productCode$.pipe(
      switchMap((code: string) => this.activeCartFacade.getEntry(code)),
      filter(isNotUndefined),
      filter((entry: OrderEntry) => !!entry && entry.basePrice?.value !== 0),
      tap((entry) => {
        if (entry?.product?.code) {
          this.displayEntry$?.next(entry);
          this.cd.markForCheck();
        }
      })
    );

    this.quantity = quantity;

    this.pickupStoreName = pickupStoreName;

    this.addedEntryWasMerged$ = this.getAddedEntryWasMerged(numberOfEntriesBeforeAdd);
  }

  cartSubtotalRounded(cart: Cart): string {
    const subTotal = cart.subTotal?.value ?? 0;
    const cartValueRounded: number = Math.round(subTotal * 100) / 100;
    return cartValueRounded.toString().replace('.', ',');
  }

  remainingAmountForFreeDelivery(cart: Cart): number {
    return this.validateCart.remainingAmountForFreeDelivery(cart);
  }

  remainingAmountForFreeDeliveryWithCommas(cart: Cart): string {
    const numberValue = this.validateCart.remainingAmountForFreeDelivery(cart).toFixed(2);
    const stringValue = numberValue.toString();
    const finalValue = stringValue.replace('.', ',');

    return finalValue;
  }

  initializeDialog() {
    this.loaded$.pipe(takeUntil(this.destroy$)).subscribe((loaded) => loaded && this.startTimer());
  }

  goToCart() {
    this.routingService.go({ cxRoute: 'cart' }, { onSameUrlNavigation: 'reload' });
    this.closeModal();
  }

  startTimer() {
    this.stopTimer();
    this.dialogCloseTimer = setTimeout(() => {
      this.closeModal();
    }, 3000);
  }

  stopTimer() {
    if (this.dialogCloseTimer) {
      clearTimeout(this.dialogCloseTimer);
      this.dialogCloseTimer = null;
    }
  }

  closeModal() {
    this.dismissModal('Close modal');
  }
}
